import { useEffect, useMemo, useState } from "react";
import Modal from "../../../../components/common/Modal";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import TextInput, { InputType } from "../../../../components/forms/textInput";
import PhoneNumberInput, {
  CountrySelect,
  emojiFlagToIsoCode,
} from "../../../../components/common/CountryPhoneInput";
import BankDocumentUpload from "./BankDocumentUpload";
import { useKcToken } from "../../../../setup/auth/useKcToken";
import { useOrganizationCode } from "../../../../setup/util/extractOrganizationCode";

import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../../../../firebase/firebase";
import { RemoteConfig } from "../../../../ampli/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { ESenderRole } from "../../../../setup/api/services/contract/models";
import { IPhoneRuleConfig } from "../../../../setup/api/services/phoneRuleConfigs/models";
import { usePhoneRuleConfigs } from "../../../../setup/util/usePhoneRuleConfigs";
import {
  buildFullPhoneNumber,
  isPhoneNumberComplete,
  isPhoneNumberValid,
  isPrefixDefinitivelyInvalid,
  SAUDI_COUNTRY_CODE,
  sanitizePhoneDigits,
  splitPhoneNumber,
} from "../../../../setup/util/phoneRuleConfig.helpers";
import {
  INTERNATIONAL_ADDRESS_MAX_LENGTH,
  INTERNATIONAL_BANK_NAME_MAX_LENGTH,
  INTERNATIONAL_BRANCH_NAME_MAX_LENGTH,
  INTERNATIONAL_IBAN_MAX_LENGTH,
  INTERNATIONAL_REGISTRATION_NUMBER_MAX_LENGTH,
  IPartyBankDocument,
  isValidIban,
  isValidSwiftCode,
  normalizeIban,
  normalizeSwiftCode,
  SAUDI_IBAN_PATTERN,
  SAUDI_NATIONAL_ADDRESS_PATTERN,
  SAUDI_UNIFIED_NUMBER_PATTERN,
} from "../../../../setup/util/partyCountry.helpers";

interface IAddPartyModalProps {
  handleClose: () => void;
  handleSave: (partyData: Inputs) => void;
  modalOpen: boolean;
  partyIndex: number;
  type: "customer" | "provider";
  mode: string;
}

export type Inputs = {
  id: string;
  rowId?: string;
  name: string;
  /** E.164 ("+966…", "+20…"); the org identifier for brokers. */
  phoneNumber: string;
  /**
   * Dial code of phoneNumber without "+"; anything but 966 makes the party international.
   * Form-only: the backend reads the dial code from phoneNumber.
   */
  phoneCountryCode?: string;
  amount: number | null;
  fee?: number;
  type: string;
  role?: string;
  isCompany?: boolean;
  /** Saudi unified number, or the free-text registration number of an international company. */
  companyUnifiedNumber?: string;
  companyName?: string;
  iban?: string;
  /** Saudi short address, or the free-text address of an international party. */
  nationalAddress?: string;
  // International parties only — see IPartyInternationalFields. The account holder is `name`.
  international?: boolean;
  bankCountry?: string;
  bankName?: string;
  swiftCode?: string;
  bankBranchName?: string;
  bankDocument?: IPartyBankDocument | null;
  // Preserved through an edit so a prefilled, non-deletable party keeps its flag.
  nonDeletable?: boolean;
};

// The national digits only live in the modal; the saved party carries the full phoneNumber.
type FormInputs = Inputs & { phoneNational: string };

const INTERNATIONAL_ONLY_FIELDS = {
  bankCountry: "",
  bankName: "",
  swiftCode: "",
  bankBranchName: "",
  bankDocument: null,
};

const countryIso = (country?: IPhoneRuleConfig) =>
  emojiFlagToIsoCode(country?.countryFlag)?.toUpperCase() || "";

const AddPartyModal = ({
  handleClose,
  modalOpen,
  handleSave,
  type,
  partyIndex,
  mode,
}: IAddPartyModalProps) => {
  const [maxPrice, setMaxPrice] = useState(1000000);
  const [documentUploading, setDocumentUploading] = useState(false);
  const { t } = useTranslation();
  const { countries, isLoading: countriesLoading } = usePhoneRuleConfigs();
  // Bank documents must be uploaded under the signed-in user's organization prefix.
  const organizationCode = useOrganizationCode(useKcToken());

  const findCountry = (countryCode?: string | null) =>
    countries.find((country) => country.countryCode === countryCode);

  const isDuplicate = (phoneNumber: string) => {
    const currentMilestoneIndex = watch("currentMilestoneIndex");
    const allParties = watch(`milestones.${currentMilestoneIndex}.parties`);

    return allParties?.some(
      (p: any, i: number) => p.phoneNumber === phoneNumber && partyIndex !== i
    );
  };

  /** The E.164 number the form currently describes; empty until the country is known. */
  const toFullPhoneNumber = (
    countryCode?: string | null,
    national?: string | null
  ) => {
    const country = findCountry(countryCode);
    if (country) return buildFullPhoneNumber(national || "", country);
    const digits = sanitizePhoneDigits(national || "");
    return countryCode && digits ? `+${countryCode}${digits}` : "";
  };

  const isPhoneValidForCountry = (
    countryCode?: string | null,
    national?: string | null
  ) => {
    const country = findCountry(countryCode);
    // A stored party whose country is no longer enabled: only sanity-check the digits.
    if (!country) return /^\d{4,14}$/.test(sanitizePhoneDigits(national || ""));
    return isPhoneNumberValid(national || "", country);
  };

  // Field types are deliberately lenient (nullish): the real rules live in superRefine, and a
  // broker skips all of them but the amount. A broker row can carry a numeric id as phoneNumber,
  // no name until the organization loads, or nulls for fields the backend echoes back — strict
  // types on those disabled/hidden fields would silently keep Save disabled.
  const schema = z
    .object({
      id: z.string(),
      name: z.string().nullish(),
      phoneNumber: z.union([z.string(), z.number()]).nullish(),
      phoneCountryCode: z.string().nullish(),
      phoneNational: z.string().nullish(),
      amount: z
        .number()
        .nullable()
        .refine((val) => val !== null, {
          message: t("err_required"),
        }),
      fee: z.number().nullish(),
      type: z.string().nonempty({ message: t("err_required") }),
      role: z.string().nullish(),
      isCompany: z.boolean().nullish(),
      companyUnifiedNumber: z.string().nullish(),
      companyName: z.string().nullish(),
      iban: z.string().nullish(),
      nationalAddress: z.string().nullish(),
      bankCountry: z.string().nullish(),
      bankName: z.string().nullish(),
      swiftCode: z.string().nullish(),
      bankBranchName: z.string().nullish(),
      international: z.boolean().nullish(),
      bankDocument: z
        .object({
          bucketName: z.string(),
          objectName: z.string(),
          fileName: z.string().nullish(),
        })
        .nullish(),
    })
    .refine((data) => data.type !== "provider" || !!data.role, {
      path: ["role"],
      message: t("err_role_required_for_providers"),
    })
    .superRefine((data, ctx) => {
      const isBroker = data.role === ESenderRole.BROKER;
      const isCompanyParty = !!data.isCompany;
      const isInternational =
        (data.phoneCountryCode || SAUDI_COUNTRY_CODE) !== SAUDI_COUNTRY_CODE;

      const requireText = (
        path: keyof FormInputs,
        value: string | null | undefined,
        max: number
      ) => {
        const trimmed = (value || "").trim();
        if (!trimmed) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [path],
            message: t("err_required"),
          });
        } else if (trimmed.length > max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [path],
            message: t("err_max_chars", { max }),
          });
        }
      };

      if (!isBroker) {
        // name is always required
        if (!data.name || data.name.trim().length < 5) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: t("err_min_chars", { min: 5 }),
          });
        } else if (data.name.trim().length > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: t("err_max_chars", { max: 100 }),
          });
        }

        // Individual/Company: require phoneNumber ALWAYS, validated against its country's rules
        if (
          !isPhoneValidForCountry(data.phoneCountryCode, data.phoneNational)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phoneNational"],
            message: isInternational
              ? t("err_invalid_phone_number")
              : t("err_invalid_phone"),
          });
        } else if (
          isDuplicate(
            toFullPhoneNumber(data.phoneCountryCode, data.phoneNational)
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phoneNational"],
            message: t("err_duplicate_phone"),
          });
        }

        if (isCompanyParty) {
          // Company: require companyName + companyUnifiedNumber
          if (!data.companyName || data.companyName.trim().length < 5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyName"],
              message: t("err_min_chars", { min: 5 }),
            });
          } else if (data.companyName.trim().length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyName"],
              message: t("err_max_chars", { max: 100 }),
            });
          }

          if (isInternational) {
            requireText(
              "companyUnifiedNumber",
              data.companyUnifiedNumber,
              INTERNATIONAL_REGISTRATION_NUMBER_MAX_LENGTH
            );
          } else if (
            !SAUDI_UNIFIED_NUMBER_PATTERN.test(data.companyUnifiedNumber || "")
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyUnifiedNumber"],
              message: t("err_invalid_unified_number"),
            });
          }
        }

        if (isInternational) {
          // Free-text address; bank country, bank name and a valid IBAN are mandatory so the
          // admin can pay out manually (SingleView can't pay international parties). The bank
          // document is optional, and the account holder is the party's name.
          if (
            (data.nationalAddress || "").trim().length >
            INTERNATIONAL_ADDRESS_MAX_LENGTH
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["nationalAddress"],
              message: t("err_max_chars", {
                max: INTERNATIONAL_ADDRESS_MAX_LENGTH,
              }),
            });
          }
          if (!data.bankCountry) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["bankCountry"],
              message: t("err_required"),
            });
          }
          requireText(
            "bankName",
            data.bankName,
            INTERNATIONAL_BANK_NAME_MAX_LENGTH
          );
          // Optional here (the business may not know it; the party can add it in the client
          // app), but it must be a well-formed BIC when given.
          if (data.swiftCode && !isValidSwiftCode(data.swiftCode)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["swiftCode"],
              message: t("err_invalid_swift_code"),
            });
          }
          if (
            (data.bankBranchName || "").trim().length >
            INTERNATIONAL_BRANCH_NAME_MAX_LENGTH
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["bankBranchName"],
              message: t("err_max_chars", {
                max: INTERNATIONAL_BRANCH_NAME_MAX_LENGTH,
              }),
            });
          }
          const iban = normalizeIban(data.iban);
          if (!iban) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["iban"],
              message: t("err_required"),
            });
          } else if (
            iban.length > INTERNATIONAL_IBAN_MAX_LENGTH ||
            !isValidIban(iban)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["iban"],
              message: t("err_invalid_iban"),
            });
          }
        } else {
          if (
            data?.nationalAddress &&
            !SAUDI_NATIONAL_ADDRESS_PATTERN.test(data.nationalAddress)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["nationalAddress"],
              message: t("err_invalid_national_address"),
            });
          }

          if (
            data?.iban &&
            !SAUDI_IBAN_PATTERN.test(normalizeIban(data.iban))
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["iban"],
              message: t("err_invalid_iban"),
            });
          }
        }

        if (
          data?.amount !== null &&
          (data?.type === "customer" ? data?.amount < 1 : data?.amount < 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["amount"],
            message: t("err_number_min", {
              min: data?.type === "customer" ? 1 : 0,
            }),
          });
        }
      } else if (data?.amount !== null && data?.amount < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amount"],
          message: t("err_number_min", { min: 0 }),
        });
      }
    });

  const { watch } = useFormContext();
  const {
    control: partyControl,
    reset,
    trigger,
    watch: partyWatch,
    setValue: setPartyValue,
    getValues: getPartyValues,
    formState: { isValid, errors, isDirty },
  } = useForm<FormInputs>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      id: "",
      name: "",
      phoneNumber: "",
      phoneCountryCode: SAUDI_COUNTRY_CODE,
      phoneNational: "",
      amount: undefined,
      fee: 0,
      role: "",
      type: "customer",
      isCompany: false,
      companyUnifiedNumber: "",
      companyName: "",
      iban: "",
      nationalAddress: "",
      ...INTERNATIONAL_ONLY_FIELDS,
    },
  });

  const roleFieldValue = useWatch({ control: partyControl, name: "role" });
  const isCompanyValue = useWatch({
    control: partyControl,
    name: "isCompany",
    defaultValue: false,
  });
  const phoneCountryCode = useWatch({
    control: partyControl,
    name: "phoneCountryCode",
  });

  const currentMilestoneIndex = watch("currentMilestoneIndex") || 0;
  const parties = watch(`milestones.${currentMilestoneIndex}.parties`);

  /** Picker country + national digits for a stored party number. */
  const phoneFieldsFor = (phoneNumber?: string) => {
    const { country, nationalNumber } = splitPhoneNumber(
      phoneNumber,
      countries
    );
    return {
      phoneCountryCode: country.countryCode,
      phoneNational: nationalNumber,
    };
  };

  useEffect(() => {
    if (parties?.[partyIndex]?.id && mode === "edit") {
      const party = parties[partyIndex];
      const isBrokerParty = party.role === ESenderRole.BROKER;
      reset({
        ...INTERNATIONAL_ONLY_FIELDS,
        ...party,
        ...(isBrokerParty
          ? { phoneCountryCode: SAUDI_COUNTRY_CODE, phoneNational: "" }
          : phoneFieldsFor(party.phoneNumber)),
        isCompany: isBrokerParty ? false : !!party.companyUnifiedNumber,
      });
      setTimeout(() => trigger(), 0);
    } else {
      reset({
        id: `${type}-${partyIndex}-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        rowId: `${type}-${partyIndex}-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        name: "",
        phoneNumber: "",
        phoneCountryCode: SAUDI_COUNTRY_CODE,
        phoneNational: "",
        amount: null,
        fee: 0,
        type,
        role: "",
        isCompany: false,
        companyUnifiedNumber: "",
        companyName: "",
        iban: "",
        nationalAddress: "",
        ...INTERNATIONAL_ONLY_FIELDS,
      });
    }
    // phoneFieldsFor reads `countries`; the effect below re-derives once they load, so they're
    // deliberately not a dependency here (that would wipe what the user typed in add mode).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyIndex, reset, parties, mode, type, trigger, modalOpen]);

  // An edited international party opened before the country list arrived was split against the
  // Saudi-only fallback; split it again against the real list.
  useEffect(() => {
    if (countriesLoading || mode !== "edit" || !modalOpen) return;
    const party = parties?.[partyIndex];
    if (!party?.id || party.role === ESenderRole.BROKER) return;
    // reset (not setValue) so the re-split values become the baseline and the form isn't dirty.
    reset({ ...getPartyValues(), ...phoneFieldsFor(party.phoneNumber) });
    setTimeout(() => trigger(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesLoading]);

  useEffect(() => {
    const fetchFirebaseConfig = async () => {
      await fetchAndActivate(remoteConfig);
      const maxPriceValue = getValue(
        remoteConfig,
        "max_product_price"
      ) as unknown as RemoteConfig;
      setMaxPrice(+maxPriceValue?._value);
    };
    fetchFirebaseConfig();
  }, []);

  useEffect(() => {
    setPartyValue("fee", 0);
  }, [setPartyValue]);

  const isBroker = roleFieldValue === ESenderRole.BROKER;
  const isInternational =
    !isBroker &&
    (phoneCountryCode || SAUDI_COUNTRY_CODE) !== SAUDI_COUNTRY_CODE;

  const selectedPhoneCountry = useMemo(
    () =>
      countries.find((country) => country.countryCode === phoneCountryCode) ||
      countries[0],
    [countries, phoneCountryCode]
  );

  const handleSelectPhoneCountry = (country: IPhoneRuleConfig) => {
    const options = { shouldDirty: true, shouldValidate: true };
    setPartyValue("phoneCountryCode", country.countryCode, options);
    // Switching country invalidates the digits typed for the previous one.
    setPartyValue("phoneNational", "", options);
    if (
      country.countryCode !== SAUDI_COUNTRY_CODE &&
      !getPartyValues("bankCountry")
    ) {
      setPartyValue("bankCountry", countryIso(country), options);
    }
  };

  const onSave = () => {
    const { phoneNational, ...data } = partyWatch();
    if (!isBroker) {
      data.phoneCountryCode = data.phoneCountryCode || SAUDI_COUNTRY_CODE;
      data.phoneNumber = toFullPhoneNumber(
        data.phoneCountryCode,
        phoneNational
      );
    }
    if (!data.isCompany) {
      data.companyUnifiedNumber = "";
      data.companyName = "";
    }
    data.iban = normalizeIban(data.iban);
    data.nationalAddress = (data.nationalAddress || "").trim();
    if (!isInternational) {
      Object.assign(data, INTERNATIONAL_ONLY_FIELDS, { international: false });
    } else {
      data.international = true;
      data.bankName = (data.bankName || "").trim();
      data.swiftCode = normalizeSwiftCode(data.swiftCode);
      data.bankBranchName = (data.bankBranchName || "").trim();
      data.companyUnifiedNumber = (data.companyUnifiedNumber || "").trim();
    }
    handleSave?.(data);
  };

  const canSave =
    (mode === "edit" ? isValid && isDirty : isValid) && !documentUploading;

  const bankCountryValue = countries.find(
    (country) => countryIso(country) === partyWatch("bankCountry")
  );

  return (
    <Modal
      customModalContainerClassName={styles.modalContainer}
      customModalClassName={styles.modal}
      visible={modalOpen}
      onCancel={() => {
        handleClose();
      }}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {type === "customer" ? t("lbl_add_customer") : t("lbl_add_provider")}
        </h1>

        {/* isCompany toggle — brokers are always individuals, so it is locked */}
        <Controller
          name="isCompany"
          control={partyControl}
          render={({ field }) => {
            const companySelected = !isBroker && !!field.value;
            return (
              <div className={styles.segmentedTabs}>
                <button
                  type="button"
                  disabled={isBroker}
                  className={`${styles.segmentedTab} ${
                    !companySelected ? styles.segmentedTabActive : ""
                  }`}
                  onClick={() => field.onChange(false)}>
                  {t("lbl_individual")}
                </button>
                <button
                  type="button"
                  disabled={isBroker}
                  className={`${styles.segmentedTab} ${
                    companySelected ? styles.segmentedTabActive : ""
                  } ${isBroker ? styles.segmentedTabDisabled : ""}`}
                  onClick={() => field.onChange(true)}>
                  {t("lbl_company")}
                </button>
              </div>
            );
          }}
        />

        {/* name is always shown */}
        <Controller
          name="name"
          control={partyControl}
          rules={{ required: true, max: 100, min: 5 }}
          render={({ field }) => (
            <TextInput
              {...field}
              maxCharacters={100}
              required
              disabled={isBroker}
              placeholder={t("lbl_name")}
              // The backend pays international parties to this name (sudoName).
              hint={
                isInternational ? t("lbl_name_account_holder_hint") : undefined
              }
              errorMessage={errors?.name?.message?.toString()}
            />
          )}
        />

        {/* phone — always shown; brokers show their identifier instead */}
        {isBroker ? (
          <Controller
            name="phoneNumber"
            control={partyControl}
            render={({ field }) => (
              <TextInput
                {...field}
                maxCharacters={100}
                required
                type={InputType.DEFAULT}
                disabled
                placeholder={t("lbl_identifier")}
                errorMessage={errors?.phoneNumber?.message?.toString()}
              />
            )}
          />
        ) : (
          <Controller
            name="phoneNational"
            control={partyControl}
            render={({ field }) => {
              // Hold the error back while the number is still being typed, unless the prefix
              // can no longer become valid.
              const showError =
                !!selectedPhoneCountry &&
                (isPhoneNumberComplete(field.value, selectedPhoneCountry) ||
                  isPrefixDefinitivelyInvalid(
                    field.value,
                    selectedPhoneCountry
                  ));
              return (
                <PhoneNumberInput
                  countries={countries}
                  selectedCountry={selectedPhoneCountry}
                  onSelectCountry={handleSelectPhoneCountry}
                  phoneNumber={field.value}
                  onPhoneChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t("lbl_mobile_number")}
                  hint={
                    isInternational ? undefined : t("lbl_mobile_number_hint")
                  }
                  errorMessage={
                    showError
                      ? errors?.phoneNational?.message?.toString()
                      : undefined
                  }
                />
              );
            }}
          />
        )}

        {/* company fields — only when isCompany */}
        {isCompanyValue && !isBroker && (
          <div className={styles.fieldRow2}>
            <Controller
              name="companyName"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  {...field}
                  maxCharacters={100}
                  required
                  placeholder={t("lbl_company_name")}
                  errorMessage={errors?.companyName?.message?.toString()}
                />
              )}
            />
            <Controller
              name="companyUnifiedNumber"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  {...field}
                  value={field.value ?? ""}
                  maxCharacters={
                    isInternational
                      ? INTERNATIONAL_REGISTRATION_NUMBER_MAX_LENGTH
                      : 10
                  }
                  required
                  hint={
                    isInternational ? undefined : t("lbl_unified_number_hint")
                  }
                  placeholder={
                    isInternational
                      ? t("lbl_company_registration_number")
                      : t("lbl_unified_number")
                  }
                  errorMessage={errors?.companyUnifiedNumber?.message?.toString()}
                />
              )}
            />
          </div>
        )}

        {isInternational ? (
          <>
            <Controller
              name="nationalAddress"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  value={field.value ?? ""}
                  name={field.name}
                  onBlur={field.onBlur}
                  handleChange={field.onChange}
                  maxCharacters={INTERNATIONAL_ADDRESS_MAX_LENGTH}
                  hint={t("lbl_address_hint")}
                  placeholder={t("lbl_address")}
                  errorMessage={errors?.nationalAddress?.message?.toString()}
                />
              )}
            />

            <h2 className={styles.sectionTitle}>
              {t("lbl_party_bank_details")}
              <span className={styles.sectionHint}>
                {t("lbl_party_bank_details_hint")}
              </span>
            </h2>

            <div className={styles.fieldRow2}>
              <Controller
                name="bankCountry"
                control={partyControl}
                render={({ field }) => (
                  <CountrySelect
                    countries={countries}
                    value={bankCountryValue}
                    onChange={(country) => {
                      field.onChange(countryIso(country));
                      field.onBlur();
                    }}
                    placeholder={t("lbl_bank_country")}
                    errorMessage={errors?.bankCountry?.message?.toString()}
                  />
                )}
              />
              <Controller
                name="bankName"
                control={partyControl}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    value={field.value ?? ""}
                    maxCharacters={100}
                    required
                    placeholder={t("lbl_bank_name")}
                    errorMessage={errors?.bankName?.message?.toString()}
                  />
                )}
              />
            </div>

            <div className={styles.fieldRow2}>
              <Controller
                name="iban"
                control={partyControl}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    value={field.value ?? ""}
                    maxCharacters={INTERNATIONAL_IBAN_MAX_LENGTH}
                    required
                    hint={t("lbl_iban_international_hint")}
                    placeholder={t("lbl_iban")}
                    errorMessage={errors?.iban?.message?.toString()}
                  />
                )}
              />
              <Controller
                name="swiftCode"
                control={partyControl}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    value={field.value ?? ""}
                    maxCharacters={11}
                    hint={t("lbl_swift_code_hint")}
                    placeholder={t("lbl_swift_code")}
                    errorMessage={errors?.swiftCode?.message?.toString()}
                  />
                )}
              />
            </div>

            <Controller
              name="bankBranchName"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  {...field}
                  value={field.value ?? ""}
                  maxCharacters={INTERNATIONAL_BRANCH_NAME_MAX_LENGTH}
                  placeholder={t("lbl_bank_branch_name")}
                  errorMessage={errors?.bankBranchName?.message?.toString()}
                />
              )}
            />

            <Controller
              name="bankDocument"
              control={partyControl}
              render={({ field }) => (
                <BankDocumentUpload
                  organizationCode={organizationCode}
                  partyId={getPartyValues("rowId") || getPartyValues("id")}
                  value={field.value}
                  onChange={(document) => {
                    field.onChange(document);
                    field.onBlur();
                  }}
                  onUploadingChange={setDocumentUploading}
                  errorMessage={errors?.bankDocument?.message?.toString()}
                />
              )}
            />
          </>
        ) : (
          <div className={styles.fieldRow2}>
            <Controller
              name="nationalAddress"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  value={field.value ?? ""}
                  name={field.name}
                  onBlur={field.onBlur}
                  handleChange={field.onChange}
                  maxCharacters={8}
                  disabled={isBroker}
                  hint={isBroker ? undefined : t("lbl_national_address_hint")}
                  placeholder={t("lbl_national_address")}
                  errorMessage={errors?.nationalAddress?.message?.toString()}
                />
              )}
            />

            <Controller
              name="iban"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  {...field}
                  value={field.value ?? ""}
                  maxCharacters={29}
                  disabled={isBroker}
                  hint={isBroker ? undefined : t("lbl_iban_hint")}
                  placeholder={t("lbl_iban")}
                  errorMessage={errors?.iban?.message?.toString()}
                />
              )}
            />
          </div>
        )}

        {type === "provider" && (
          <Controller
            name="role"
            control={partyControl}
            rules={{ required: true }}
            render={({ field }) => (
              <TextInput
                {...field}
                maxCharacters={100}
                required
                value={
                  field?.value === ESenderRole.BROKER
                    ? t("lbl_arbitrator_title")
                    : field?.value
                }
                disabled={isBroker}
                placeholder={t("lbl_role")}
                errorMessage={errors?.role?.message?.toString()}
              />
            )}
          />
        )}

        <Controller
          name="amount"
          control={partyControl}
          rules={{
            required: true,
            max: maxPrice,
            min: type === "customer" ? 1 : 0,
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field?.value !== null ? field?.value : ""}
              onChange={(event) => {
                field?.onChange(
                  event?.target?.value === "" ? null : +event?.target?.value
                );
              }}
              maxCharacters={100}
              required
              description={t("lbl_saudi_currency")}
              hint={type === "customer" ? t("lbl_amount_hint") : undefined}
              type={InputType.NUMBER}
              placeholder={t("lbl_value")}
              errorMessage={errors?.amount?.message?.toString()}
            />
          )}
        />

        <PrimaryButton
          disabled={!canSave}
          style={{
            marginTop: "8px",
            background: canSave ? "#0D44AD" : "#EEF0F5",
          }}
          onClick={onSave}>
          {mode === "add" ? t("lbl_add") : t("lbl_edit")}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default AddPartyModal;
